import React, { useState } from "react";
import { Target } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import { getErrorMessage } from "../../types";

export interface AddSkillBenchmarkModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  workspace: { slug: string; name: string } | null;
  onSuccess: () => Promise<void>;
}

export const AddSkillBenchmarkModal: React.FC<AddSkillBenchmarkModalProps> = ({
  isOpen,
  onClose,
  slug,
  workspace,
  onSuccess,
}) => {
  const [skillName, setSkillName] = useState("");
  const [targetProficiency, setTargetProficiency] = useState("Intermediate");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setSkillName("");
    setTargetProficiency("Intermediate");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug || !workspace || !skillName.trim()) return;

    try {
      setSaving(true);
      setError(null);
      const { data, error: apiError } = await api.createWorkspaceSkillExpectationApiV1B2bSlugWorkspacesWsSlugSkillsPost({
        path: { slug, ws_slug: workspace.slug },
        body: {
          skill_name: skillName.trim(),
          target_proficiency: targetProficiency,
        },
      });

      if (data) {
        handleClose();
        await onSuccess();
      } else {
        setError(getErrorMessage(apiError, "Failed to create skill benchmark"));
      }
    } catch (err) {
      console.error("Error creating skill benchmark:", err);
      setError("Network error creating skill benchmark");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Skill Benchmark"
      subtitle={workspace ? `For workspace: ${workspace.name}` : undefined}
      icon={<Target className="w-5 h-5 text-purple-600" />}
      maxWidth="max-w-md"
    >
      {error && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-semibold">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 mb-1">Skill Name *</label>
          <input
            type="text"
            required
            placeholder="e.g. Distributed Systems, Kubernetes, Cloud Security"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-purple-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1">Target Proficiency *</label>
          <select
            value={targetProficiency}
            onChange={(e) => setTargetProficiency(e.target.value)}
            className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-purple-500"
          >
            <option value="Novice">Novice (Foundational Knowledge)</option>
            <option value="Intermediate">Intermediate (Working Competency)</option>
            <option value="Advanced">Advanced (Autonomous Delivery)</option>
            <option value="Expert">Expert (Strategic Architecture & Leadership)</option>
          </select>
        </div>

        <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !skillName.trim()}
            className="px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-sm transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Benchmark"}
          </button>
        </div>
      </form>
    </Modal>
  );
};
